import { trackPageView } from "./snowplowEventTrackers";
import { enableSnowplowPlugins } from "./snowplowPlugins";
import { composeChannelCategoryContextData } from "./snowplowTracking";
import { getDynamicSchemaContext, getSnowplowVariables, hasUserAcceptedMeasurementConsent, getAvailableTopics, getSchemaName } from "./utils";


export async function snowplowInit() {
  // Treat jest presence as test environment; avoid referencing process (not defined in browser build)
  const isTestEnv = typeof jest !== 'undefined';
  if (isTestEnv || !window?.snowplow) {
    return;
  }


  const { appId, collectorUrl, nameSpace } = getSnowplowVariables();

  // Check specifically for C0002 (measurement) consent for Snowplow cookies
  const hasMeasurementConsent = hasUserAcceptedMeasurementConsent();

  const snowplowConfig = {
    appId,
    cookieSameSite: 'Lax',
    cookieName: "mls_sp",
    // Configure based on C0002 measurement consent
    anonymousTracking: hasMeasurementConsent ? false : true,
    // Use storage strategy based on measurement consent
    stateStorageStrategy: hasMeasurementConsent ? 'cookieAndLocalStorage' : 'none',
    contexts: {
      webPage: true,
      session: hasMeasurementConsent,
      performanceNavigationTiming: true,
      performanceTiming: true,

    }
  };

  window.snowplow('newTracker', nameSpace, collectorUrl, snowplowConfig);

  enableSnowplowPlugins();
  await onConsentChanged();
  trackPageLoad();
}

export function trackPageLoad() {

  const { entityContexts, pageType } = getSnowplowVariables();

  if (!entityContexts?.length) {
    if (pageType === "video") {
      const videoSlug = window.location.hash.substring(1) ?? "";
      const selectedVideo = videoSlug.length > 0 && window.forgeVariables.tracking.videoPlaylist.find(video => videoSlug === video.slug) ? window.forgeVariables.tracking.videoPlaylist.find(video => videoSlug === video.slug) : window.forgeVariables.tracking.videoPlaylist[0];
      window.forgeVariables.tracking.videoPlaylist = { ...selectedVideo, topics: selectedVideo.tags };
    }

    const contexts = [composeChannelCategoryContextData()];
    const schemaName = getSchemaName();
    const dynamicSchemaContext = getDynamicSchemaContext(schemaName);


    if (pageType === "news" || pageType === "video") {
      const topics = getAvailableTopics(schemaName);

      if (topics?.length) {
        contexts.push(...topics);
      }
    }

    if (dynamicSchemaContext) {
      contexts.push(dynamicSchemaContext);
    }

    trackPageView({
      contexts,
    });
  }
}



export async function onConsentChanged() {
  // Check for C0002 measurement consent on consent change
  const hasMeasurementConsent = hasUserAcceptedMeasurementConsent();

  if (!hasMeasurementConsent) {
    // No measurement consent - enable anonymous tracking with no storage
    clearUserTracking();
    window.snowplow("enableAnonymousTracking", {
      stateStorageStrategy: 'none'
    });
  } else {
    // Has measurement consent - disable anonymous tracking and use cookies
    if (window?.auth0Client) {
      window.snowplow("disableAnonymousTracking", {
        stateStorageStrategy: 'cookieAndLocalStorage'
      });
      await setupUserIdIfLoggedIn();
    }
  }
}

export async function setupUserIdIfLoggedIn() {
  if (window?.auth0Client) {
    const user = await window?.auth0Client?.getUser();
    if (user?.sub) {
      window.snowplow("disableAnonymousTracking", {
        stateStorageStrategy: 'cookieAndLocalStorage'
      });
      window?.snowplow?.('setUserId', user?.sub);
    }
  }
}

export async function clearUserTracking() {
  const isTestEnv = typeof jest !== 'undefined';
  if (isTestEnv) {
    return;
  }

  window.snowplow('clearUserData', { preserveSession: false, preserveUser: false });
  window.snowplow("enableAnonymousTracking", {
    stateStorageStrategy: 'none'
  });
}

if (typeof window !== 'undefined') {
  window.snowplowInit = snowplowInit;
  window.onConsentChanged = onConsentChanged;
}
